import Timeline from "@mui/icons-material/Timeline";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { Link } from "react-router-dom";

import {
  CersSubmittalRow,
  decisionStatusColor,
  facilityTimelineUrl,
  formatDate,
} from "./cersStatus";

type CersModuleDecision = CersSubmittalRow["moduleDecisions"][number];

type Props = {
  /** The selected row, or null when the drawer is closed. */
  submittal: CersSubmittalRow | null;
  onClose: () => void;
};

function ModuleDecisionRow({ decision }: { decision: CersModuleDecision }) {
  const hasContact = decision.regulatorName || decision.regulatorEmail || decision.regulatorPhone;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">{prettyPrintEnumValue(decision.module)}</Typography>
        <StatusChip
          label={prettyPrintEnumValue(decision.status)}
          color={decisionStatusColor(decision.status)}
        />
        <Typography variant="caption" color="text.secondary">
          {formatDate(decision.decisionDate)}
        </Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        CUPA: {decision.cupaCode ?? "—"}
      </Typography>

      {/* Verbatim CUPA comment — never reworded or trimmed. */}
      {decision.rawComment ? (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
          {decision.rawComment}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          No comment
        </Typography>
      )}

      {hasContact && (
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {decision.regulatorName && (
            <Typography variant="caption" color="text.secondary">
              {decision.regulatorName}
            </Typography>
          )}
          {decision.regulatorEmail && (
            <Typography variant="caption" color="text.secondary">
              {decision.regulatorEmail}
            </Typography>
          )}
          {decision.regulatorPhone && (
            <Typography variant="caption" color="text.secondary">
              {decision.regulatorPhone}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Read-only detail drawer for a single CERS submittal: header (facility /
 * organization / submitted date / submitted by) plus the full per-module
 * decision breakdown with verbatim CUPA comments. No resolution actions —
 * this is a browser, not a workflow surface.
 */
export function SubmittalDetailDrawer({ submittal, onClose }: Props) {
  const open = submittal !== null;

  return (
    <RightDrawerShell open={open} onClose={onClose} title={submittal?.facilityName ?? ""}>
      {submittal && (
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {submittal.organizationName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submitted {formatDate(submittal.submittedDate)}
              {submittal.submittedBy ? ` by ${submittal.submittedBy}` : ""}
            </Typography>
            {submittal.facilityName && (
              <Box sx={{ mt: 1 }}>
                <Button
                  component={Link}
                  to={facilityTimelineUrl(submittal.facilityName)}
                  startIcon={<Timeline />}
                  size="small"
                  variant="outlined"
                >
                  View facility timeline
                </Button>
              </Box>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Module decisions
            </Typography>
            {submittal.moduleDecisions.length > 0 ? (
              <Stack spacing={2} divider={<Divider flexItem />}>
                {submittal.moduleDecisions.map((decision) => (
                  <ModuleDecisionRow key={decision.id} decision={decision} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No module decisions recorded.
              </Typography>
            )}
          </Box>
        </Stack>
      )}
    </RightDrawerShell>
  );
}
